import { useEffect, useRef, useState } from "react"
import { Box, InputBase } from "@mui/material"
import { useTheme } from "@mui/styles"
import { ImgBase } from "@components/images"
import { Column, Row, Text, FieldLabel, HSpace } from "@layout"

// A searchable single-select.
//
// Deliberately not built on MUI's Autocomplete: that stamps an inline
// `width: anchorEl.clientWidth` onto its popper and portals it to <body>, and
// `componentsProps.popper` does not exist in MUI 5.6.x, so the option list cannot be
// kept to the field's width. Here the panel is an absolutely-positioned child of the
// field box, so its width is structural (left/right pinned) rather than measured.
//
// `options` are `{ id, label, ... }`; `value` is the selected option object (or null).
const SelectSearchable = ({
  label,
  value,
  options = [],
  placeholder,
  searchPlaceholder,
  noOptionsText,
  required,
  error,
  disabled,
  width = "100%",
  onChange
}) => {
  const theme = useTheme()
  const [open, setOpen] = useState(false)
  const [keyword, setKeyword] = useState("")
  const rootRef = useRef(null)

  useEffect(() => {
    if (!open) return

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false)
    }
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false)
    }

    document.addEventListener("mousedown", handlePointerDown)
    document.addEventListener("keydown", handleKeyDown)
    return () => {
      document.removeEventListener("mousedown", handlePointerDown)
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [open])

  const query = keyword.trim().toLowerCase()
  const filtered = query ? options.filter((item) => item.label.toLowerCase().includes(query)) : options

  const handleToggle = () => {
    if (disabled) return
    setKeyword("")
    setOpen((prev) => !prev)
  }

  const handlePick = (option) => {
    onChange(option)
    setOpen(false)
    setKeyword("")
  }

  const borderColor = error ? theme.border.red : theme.border.gray_3

  return (
    <Box ref={rootRef} sx={{ position: "relative", width }}>
      {label ? (
        <FieldLabel size="14px" weight="500" required={required}>
          {label}
        </FieldLabel>
      ) : null}
      <Row
        onClick={handleToggle}
        sx={{
          // mirrors InputText's box model (2px 8px padding, 1px border) and MUI's
          // 1.4375em input line box, so it sits flush with neighbouring fields
          padding: "2px 8px",
          border: `1px solid ${borderColor}`,
          borderRadius: "2px",
          backgroundColor: theme.palette.background[disabled ? "gray_2" : "default"],
          cursor: disabled ? "default" : "pointer",
          justifyContent: "space-between",
          "& p": { lineHeight: "1.4375em" }
        }}
      >
        <Text
          size="14px"
          color={value ? "primary" : "gray_9"}
          whitespace="nowrap"
          overflow="hidden"
          textoverflow="ellipsis"
        >
          {value ? value.label : placeholder}
        </Text>
        <HSpace length={8} />
        <ImgBase srcx="bx-bxs-down-arrow@3x" width="10px" alt="open list" />
      </Row>

      {open ? (
        // plain Box, not Column: @layout's Column hardcodes `position: relative` via
        // @mui/styles, and with no StylesProvider injectFirst that JSS rule beats an
        // emotion `sx` — the panel would drop back into flow and stretch the page.
        // left/right pinned to this box, so the panel is exactly the field's width
        <Box
          sx={{
            position: "absolute",
            display: "flex",
            flexDirection: "column",
            left: 0,
            right: 0,
            top: "100%",
            zIndex: 20,
            marginTop: "2px",
            border: `1px solid ${theme.border.gray_3}`,
            borderRadius: "2px",
            backgroundColor: theme.palette.background.default,
            boxShadow: "0 6px 18px rgba(40, 30, 60, 0.18)",
            overflow: "hidden"
          }}
        >
          <Box
            sx={{
              padding: "6px 8px",
              borderBottom: `1px solid ${theme.border.gray_10}`,
              backgroundColor: theme.palette.background.gray_2
            }}
          >
            <InputBase
              autoFocus
              fullWidth
              value={keyword}
              placeholder={searchPlaceholder}
              onChange={(event) => setKeyword(event.target.value)}
              sx={{ fontSize: "14px", "& .MuiInputBase-input": { padding: 0 } }}
            />
          </Box>
          <Column sx={{ maxHeight: "240px", overflowY: "auto" }}>
            {filtered.length ? (
              filtered.map((option) => (
                <Box
                  key={`searchable_option_${option.id}`}
                  onClick={() => handlePick(option)}
                  sx={{
                    padding: "8px",
                    cursor: "pointer",
                    fontSize: "14px",
                    backgroundColor:
                      option.id === value?.id ? theme.palette.background.gray_2 : theme.palette.background.default,
                    "&:hover": { backgroundColor: theme.palette.background.gray_2 }
                  }}
                >
                  {option.label}
                </Box>
              ))
            ) : (
              <Box sx={{ padding: "12px 8px", textAlign: "center" }}>
                <Text size="13px" color="gray_9">
                  {noOptionsText}
                </Text>
              </Box>
            )}
          </Column>
        </Box>
      ) : null}
    </Box>
  )
}

export default SelectSearchable
